/**
 * SCORM 1.2仕様より: 真理値
 */
export type ScormResult = 'true' | 'false';

export const isScormResult = (
	value: string | null | undefined
): value is ScormResult => value === 'true' || value === 'false';

/**
 * エラーコード
 * エラーを識別する整数番号に変換可能なものである．
 * 100番台一般的エラー
 * 200番台構文エラー
 * 300番台 LMSエラー
 * 400番台データモデルエラー
 * 次のコードがエラー・メッセージに利用可能である．
 * 0 エラーなし
 * 101 一般的な例外
 * 201 不正引数エラー
 * 202 要素は子要素を持たない
 * 203 要素は配列でない‐countを持たない
 * 301 未初期化
 * 401 非実装エラー
 * 402 無効な値設定呼出し，要素はキーワード
 * 403 要素は読み出し専用
 * 404 要素は書き込み専用
 * 405 不正データ・タイプ
 * 追加コードは未定義
 */
export type ScormErrorCode =
	/**
	 * エラーなし
	 */
	| '0'
	/**
	 * 一般的な例外
	 */
	| '101'
	/**
	 * 不正引数エラー
	 */
	| '201'
	/**
	 * 要素は子要素を持たない
	 */
	| '202'
	/**
	 * 要素は配列でない‐countを持たない
	 */
	| '203'
	/**
	 * 未初期化
	 */
	| '301'
	/**
	 * 非実装エラー
	 */
	| '401'
	/**
	 * 無効な値設定呼出し，要素はキーワード
	 */
	| '402'
	/**
	 * 要素は読み出し専用
	 */
	| '403'
	/**
	 * 要素は書き込み専用
	 */
	| '404'
	/**
	 * 不正データ・タイプ
	 */
	| '405';

export const isScormErrorCode = (value?: string): value is ScormErrorCode => {
	switch (value) {
		case '0':
		case '101':
		case '201':
		case '202':
		case '203':
		case '301':
		case '401':
		case '402':
		case '403':
		case '404':
		case '405':
			return true;
		default:
			return false;
	}
};

/**
 * 未詳: SCORM 1.2 パラメータ型
 */
export type ScormParameter = string;

/**
 * SCORM 1.2 標準API型
 */
// tslint:disable-next-line: class-name
export interface IScorm1_2Api {
	/**
	 * 記述: この機能は，APIアダプタに対して，SCOがこれからLMSと通信することを示す．これにより，LMSは，LMS固有の初期化項目を取り扱うことができる．SCOは，他のどのAPI機能を呼ぶよりも前にこの機能を呼ばなくてはならない．
	 * 構文: LMSInitialize(パラメータ)
	 * パラメータ: “” この標準に適合するためには，空文字列(“”)を渡されなければならない．空文字列(“”)以外の値は将来の拡張のために予約されている．
	 * 返り値: 文字列は真理値を表わす．
	 *   「True」結果は，LMSInitialize("")が成功したことを示す．
	 *   「False」結果は，LMSInitialize("")が失敗だったことを示す．
	 * 「False」の返り値が返された場合，LMSが未知の状態であり，以降のいかなるAPI呼び出しもLMSによって処理されないことをSCOに示す．
	 *
	 * @example
	 *
	 * var result = LMSInitialize("")
	 * if (result == "false")
	 * {
	 * // Do some error handling
	 * }
	 * else
	 * {
	 * // continue with the execution of the SCO
	 * }
	 *
	 * @remarks
	 * SCOは，コンテントがLMSとのコミュニケーションを確立したいとAPIアダプタに伝える．典型的な返り値は「True」．
	 */
	LMSInitialize: (_: '') => ScormResult;

	/**
	 * 記述: SCOは，この時点より前にLMSInitializeの呼び出しに成功した後，LMSとの交信が不要と判断すれば，この関数を呼びださなければならない．この呼び出しは2つのことを意味する:
	 * 1. LMSSetValue()呼び出しを使用したいかなる設定データもLMSによって保持されることがSCOに対して保証される．
	 * 2. SCOはLMSとの通信を終了した．
	 * 構文: LMSFinish(parameter)
	 * パラメータ："" この標準に適合するためには，空文字列("")を渡されなけらばならない．空文字列("")以外の値は将来の拡張のために予約されている．
	 * 返り値: 真理値を表わす文字列
	 *   「True」結果は，LMSFinish("")が成功したことを示す．
	 *   「False」結果は，LMSFinish("")が失敗だったことを示す．
	 * 「True」の返り値が返された場合，SCOは他のAPI関数をそれ以上呼んではならない．
	 * 「False」の返り値が返された場合，これはLMSが未知の状態であり，以降のいかなるAPI呼び出しも，LMSによって処理されるか処理されないか不明であることをSCOに示す．
	 *
	 * @example
	 *
	 * var result = LMSFinish("");
	 */
	LMSFinish: (_: '') => ScormResult;

	/**
	 * 記述: この機能により，SCOはLMSから情報を取得できる．この機能は以下を決定するために使用される:
	 *   データモデルの様々なカテゴリー(グループ)および要素の値
	 *   サポートされているデータモデルのバージョン
	 *   特定のカテゴリーや要素がサポートされているかどうか．
	 *   配列や要素リスト中の現在のアイテム数
	 * 完全なデータ要素名かキーワードがパラメータとして提供される．要求されたデータモデルパラメーターの現在値が返される．1つの値だけ(常に文字列)が呼び出しごとに返される．
	 * 構文: LMSGetValue(パラメータ)
	 * パラメータ:
	 * datamodel.group.element
	 * 指定された要素の値を返す．
	 * datamodel._version
	 * _versionキーワードはLMSによってサポートされるデータモデルのバージョンを決定するために使用される．
	 * datamodel.element._count
	 * _countキーワードは現配列中の要素の数を決定するために使用される．数は配列中の最終位置の表示ではなく配列中の要素の総数である．
	 * datamodel.element._children
	 * _childrenキーワードはLMSによってサポートされるグループもしくはカテゴリ中すべての要素を決定するために使用される．
	 * 返り値: 返り値はすべて文字列である．
	 * LMSGetValue(datamodel.group.element)
	 * 返り値は要求された要素あるいはグループの現在値を表わす列である．
	 * LMSGetValue(datamodel._version)
	 * 返り値はLMSによってサポートされたデータモデルのバージョンを表わす列である．
	 * LMSGetValue(datamodel.group._children)
	 * 戻り値は，LMSによってサポートされる，指定のグループまたはカテゴリ中のすべての要素リスト名をコンマで区切ったものである．要素に子はないが，要素がサポートされる場合，空文字列("")が返される．要素がサポートされない場合も，空文字列("")が返される．要素がサポートされない場合，引き続いて最後のエラーを要求する[LMSGetLastError()]ことで，要求がサポートされているかどうかを決定できる．
	 * エラー「401 Not implemented error」は要素がサポートされないことを示す．
	 * LMSGetValue(datamodel.group._count)
	 * 返り値は現在の要素リストあるいは配列中のアイテム数を示す整数である．
	 *
	 * @example
	 *
	 * var value = LMSGetValue("cmi.core.student_name")
	 *
	 * @remark 典型的な返り値は“Hyde,Jackson”といったものである．
	 *
	 * @example
	 *
	 * var value = LMSGetValue("cmi.core.lesson_status")
	 *
	 * @remark 典型的な返り値は“incomplete”(非完了)である．
	 *
	 * @example
	 *
	 * var value = LMSGetValue("mi._version")
	 *
	 * @remark CMIデータモデルを定義するIEEEのドキュメントの現在の規格案は，Computer Managed Instruction規格案というタイトルで，IDがP1484.11.2，バージョンが3.4である．この呼び出しは，IEEEのドキュメントのバージョン番号3.4を返す．
	 *
	 * @example
	 *
	 * var value = LMSGetValue("cmi.student_preferences._children")
	 *
	 * @remark これはカテゴリサポート情報の要求である．典型的な1つの返り値は“audio,speed,text”である．空文字列(“”)が返される場合，student_preferencesはサポートされていない．最後のエラーを決定する追加のAPI呼び出しでこれを確認できる．
	 */
	LMSGetValue: (parameter: ScormParameter) => string;

	/**
	 * 記述: この機能により，SCOはLMSに情報を送ることができる．APIアダプタは，LMSへ直ちに情報を転送するか，あるいは他のアプローチに基づいて情報を転送するように設計される．
	 * この機能はデータ・モデル中の様々なカテゴリー(グループ)や要素に現在値を設定するために使用される．
	 * データ要素名およびそのグループがパラメータとして提供される．呼び出しの第2のパラメータとして，データ要素の新しく設定したい値が含まれる．各呼び出しでは1つの値だけが送られる．
	 * 構文: LMSSetValue(パラメータ,値)
	 * パラメータ: これはデータモデルに定義された完全適格要素名である．引数は大文字と小文字が区別される．引数は引用符で囲まれた文字列である．
	 * 下記はこのパラメータがとり得るいくつかの形式を表わす．
	 * datamodel.element
	 * これはデータモデル(Data Model)の中で定義されたカテゴリあるいはグループの名前である．一つの例は“cmi.comments”である．
	 * datamodel.group.element
	 * これはデータモデル(Data Model)の中で定義された要素の名前である．一つの例は“cmi.core.lesson_status”である．
	 * datamodel.group.n.element
	 * 要素配列(ゼロベース方式のインデックス付けが使用される)のn -1番目のメンバのサブ要素の値．
	 * 値: 第1パラメータで指定されるデータモデル要素に対して，この標準規格で定義されたデータ型に変換可能でなくてはならない文字列である．
	 * 返り値:文字列は真理値を表わす．
	 *   “True”結果は，LMSSetValue()が成功したことを示す．
	 *   “False”結果は，LMSSetValue()が失敗だったことを示す．
	 * @example
	 * var result = LMSSetValue("cmi.core.score.raw","95");
	 * cmi.core.score.rawに値95を設定
	 * LMSGetValue("mi.core.score.raw")に続く呼び出しは，95を返さなければならない．
	 */
	LMSSetValue: (parameter: ScormParameter, value: string) => ScormResult;

	/**
	 * 記述: SCOからLMSSetValue()で受け取った値をAPIアダプタがキャッシングしている場合，この呼び出しは，LMSがまだ保持していないこれらすべての値を保持するように要求する．
	 * いくつかの実装では，APIアダプタは設定値を受け取ると直ちに保持し，それらをクライアントにキャッシュしない．そのような実装では，このAPI呼び出しは冗長で，APIアダプタからは何の追加アクションも起きない．この呼び出しは，LMSSetValue()呼び出しによる送信データがLMSCommit()の完了時にLMSによって保持されることをSCOに保証する．
	 * 構文: LMSCommit(パラメータ)
	 * パラメータ: "" この標準に適合するためには，空文字列("")を渡されなけらばならない．空文字列("")以外の値は将来の拡張のために予約されている．
	 * 返り値: 真理値を表わす文字列
	 *   “True”結果は，LMSCommit("")が成功したことを示す．
	 *   “False”結果は，LMSCommit("")が失敗だったことを示す．
	 * 「False」の返り値が返された場合，これはLMSが未知の状態であり，以降のいかなるAPI呼び出しも，LMSによって処理されるか
	 * 処理されないか不明であることをSCOに示す．
	 * @example
	 * var result = LMSCommit("");
	 * @remark キャシュされた任意の値は，SCOのLMSSetValue()呼び出しを経由して以前に設定されたもので，LMSによって持続されていない
	 * 値が持続されるよう要求する．
	 */
	LMSCommit: (_: '') => ScormResult;

	/**
	 * 記述: SCOは，すべてのAPI呼び出しが成功したか否か，またもし不成功の場合，何が悪かったかを評価する方法を持たねばならない．この関数は，前のAPI呼び出しに起因するエラー・ステータス・コードを返す．(この関数，LMSGetErrorStringおよびLMSGetDiagnostic―すなわちエラー関数―を除いて)API関数が呼ばれるごとにエラーコードがリセットされる．SCOはエラーコードを検索するためにエラー関数を何回も呼び出すことができ，次のAPI呼び出しが行われるまで，そのコードは変わらない．
	 * 構文: LMSGetLastError()
	 * パラメータ: 無し
	 * 返り値:
	 * 返り値は文字列で，以下のカテゴリに属するエラーを識別する整数番号に変換可能なものである．
	 * 100番台一般的エラー
	 * 200番台構文エラー
	 * 300番台 LMSエラー
	 * 400番台データモデルエラー
	 * 次のコードがエラー・メッセージに利用可能である．
	 * 0 エラーなし
	 * 101 一般的な例外
	 * 201 不正引数エラー
	 * 202 要素は子要素を持たない
	 * 203 要素は配列でない‐countを持たない
	 * 301 未初期化
	 * 401 非実装エラー
	 * 402 無効な値設定呼出し，要素はキーワード
	 * 403 要素は読み出し専用
	 * 404 要素は書き込み専用
	 * 405 不正データ・タイプ
	 * 追加コードは未定義
	 * @example
	 * var errorCode = LMSGetLastError();
	 */
	LMSGetLastError: () => ScormErrorCode;

	/**
	 * 記述: この関数により，コンテンツは，エラーコード番号によって表わされるエラーの記述を得ることができる．
	 * 構文: LMSGetErrorString(errornumber)
	 * パラメータ: エラーコードを表わす整数番号．
	 * 返り値: エラー記述を表現する文字列．
	 * @example
	 * var errorString = LMSGetErrorString("403");
	 * @remark errorStringには”Element is read only”(要素は読み出し専用)という文字列設定されなければならない．
	 */
	LMSGetErrorString: (errornumber: ScormErrorCode) => string;

	/**
	 * 記述: この機能はベンダー特有のエラー記述の開発及びコンテントによるアクセスを可能にする．これらは，通常，エラーに関する補足詳細を提供する．
	 * 構文: LMSGetDiagnostic(parameter)
	 * パラメータ: パラメータは，2つの形式のうちの1つをとる．
	 *   エラーコードを表わす整数番号．これは，リストされたエラーコードについての補足情報を要求するものである．
	 *   ""空文字列．これは，発生した最後のエラーについての補足情報を要求するものである．
	 * 返り値: 返り値は，要求されたエラーあるいは最後のエラーのいずれかに関係する任意のベンダーに要望する追加の情報を表わす文字列である．
	 * @example
	 * var moreInfo = LMSGetDiagnostic("403");
	 * @remark moreinfoは，”Element is read only”(要素は読み出し専用)というエラーに関する，ベンダー特有の追加情報を含んでよい．
	 */
	LMSGetDiagnostic: (errornumber: ScormErrorCode) => string;
}



// WEBPACK FOOTER //
// ./src/logic/scorm/api/1_2.ts